<script lang="ts">
	import classnames from 'classnames';
	// Utils
	import type { CourseModel, SessionModel } from '@24hbs/utils';
	import { dateFullMonthFormat, isValidDate, removeMasterCorsoLaurea } from '$lib/utils/formatter';
	import { lessionModeText, lessonModeIcon } from '$lib/utils/course-utils';
	import { handleUrlClientSide } from '$lib/utils/assets-utils';
	import { getShortedSlugFromFull, hasShortSlug } from '$lib/utils/courses/slug';
	import { omit } from '$lib/utils/object';
	// Components
	import { Icon } from '$lib/components/atoms/Icon';
	import { BadgeBestSeller } from '$lib/components/atoms/Badge';
	import { Price } from '$lib/components/atoms/Price';
	import { PUBLIC_CODING_ACADEMY, PUBLIC_STATIC_ASSETS_BASE_URL } from '$env/static/public';
	import ImageComponent from '$lib/components/atoms/Image/ImageComponent.svelte';
	import { useCourseTypeUtils } from '$lib/utils/courses/type-utils';
	import type { ICourseTypeFields } from '$lib/utils/contentful/types';
	import { onMount } from 'svelte';

	const attributes = omit($$restProps, 'class');

	export let course: CourseModel;
	export let session: SessionModel = null;
	export let showData: boolean = true;

	export let useSessionLink: boolean = false;
	export let customLink: string = '';
	export let link: boolean = false;
	export let isPowertag: boolean = false;

  export let isPriceHidden: boolean = false;

	const mainSession = session === null ? course.sessions[course.idMainSession] : session;

	const courseTypeUtils = useCourseTypeUtils(fetch);
	let courseType: ICourseTypeFields | null;

	onMount(async () => {
		if (mainSession && mainSession.courseTypeFull) {
			courseType = await courseTypeUtils.getCourseTypeByName(mainSession.courseTypeFull);
		}
	});

	const courseLink = () => {
		if (customLink) return customLink;

		if (hasShortSlug(course.slug)) {
			return `/${getShortedSlugFromFull(course.slug)}`;
		}
		return `/formazione/${course.slug}${useSessionLink ? '/' + mainSession.idSession : ''}`;
	};
</script>

<section
	class={classnames('course-card-wide', $$props.class)}
	{...attributes}
>
	<div class="course-media">
		{#if course.media}
			<div class="course-video">
				<video
					autoplay="autoplay"
					playsinline=""
					muted="muted"
					loop="loop"
					preload="none"
					data-src={course.media}
					class="course-video"
				>
					<source src={course.media} />
				</video>
			</div>
		{:else if course && (course.bannerUrl !== PUBLIC_STATIC_ASSETS_BASE_URL || course.previewUrl !== PUBLIC_STATIC_ASSETS_BASE_URL)}
			<div class="course-image">
				{#if course.bannerUrl !== PUBLIC_STATIC_ASSETS_BASE_URL}
					<ImageComponent
						class="image"
						src={handleUrlClientSide(course.bannerUrl)}
						alt={removeMasterCorsoLaurea(course.title)}
					/>
				{:else}
					<ImageComponent
						class="image"
						src={handleUrlClientSide(course.previewUrl)}
						alt={removeMasterCorsoLaurea(course.title)}
					/>
				{/if}
			</div>
		{/if}

		<div class="course-action">
			<slot name="media" />
		</div>
	</div>
	<div class="content">
		{#if course.bestSeller}
			<div class="course-badge">
				<BadgeBestSeller />
			</div>
		{/if}
		<div class="course-block">
			<div class="course-header">
				<p class="course-type">
					{#if mainSession && mainSession.productCode !== PUBLIC_CODING_ACADEMY}
						{course.courseTypeFull}
					{:else}
						CODING ACADEMY
					{/if}
				</p>

				{#if link}
					<a class="course-link" href={courseLink()}>
						{#if isPowertag}
							<h2 class="course-title">
								{course.title}
							</h2>
						{:else}
							<h1 class="course-title">
								{course.title}
							</h1>
						{/if}
					</a>
				{:else if isPowertag}
					<h2 class="course-title">
						{course.title}
					</h2>
				{:else}
					<h1 class="course-title">
						{course.title}
					</h1>
				{/if}
			</div>
			{#if showData}
				<div class="course-data-container">
					<ul class="course-data">
						{#if isValidDate(mainSession.startDate)}
							<li class="course-data-item">
								<span class="data-icon">
									<Icon icon="calendar" />
								</span>
								<span class="data-text">
									{dateFullMonthFormat(mainSession.startDate)}
								</span>
							</li>
						{/if}
						{#if mainSession && mainSession.duration && mainSession.durationType}
							<li class="course-data-item">
								<span class="data-icon">
									<Icon icon="duration" />
								</span>
								<span class="data-text">
									{mainSession.duration}
									{mainSession.durationType}
								</span>
							</li>
						{/if}
						{#if mainSession && mainSession.lessionMode}
							<li class="course-data-item full">
								<span class="data-icon">
									<Icon icon={lessonModeIcon(mainSession.lessionMode)} />
								</span>
								<span class="data-text">
									{lessionModeText(mainSession.lessionMode).toUpperCase()}
								</span>
							</li>
						{/if}
						{#if course.careerProspect}
							<li class="course-data-item full">
								<span class="data-icon">
									<Icon icon="stage" />
								</span>
								<span class="data-text">
									{course.careerProspect}
								</span>
							</li>
						{/if}
					</ul>
				</div>
        {#if !isPriceHidden}
          {#if courseType?.slug !== 'executive-mba'}
            <div class="course-price">
              <Price showLastSeats session={mainSession} />
            </div>
          {/if}
        {/if}
			{/if}
		</div>
	</div>
</section>

{#if $$slots.footer}
	<div class="footer grid gap-y-4 xs:flex xs:justify-end xs:space-x-8">
		<slot name="footer" />
	</div>
{/if}

<style type="text/scss" lang="scss">
	.course-card-wide {
		@apply relative;
		@apply grid md:grid-cols-5 gap-x-0;
		.course-link {
			@apply inline-block mb-5;
			@apply text-gray-900 font-medium font-serif;

			&:after {
				content: '';
				@apply absolute inset-0;
				@apply z-10;
			}
		}
		.course-image,
		.course-video {
			@apply h-full;

			> * {
				@apply absolute w-full h-full;
				@apply inset-0;
			}
		}

		.course-video {
			> * {
				@apply pb-0 object-cover;
			}
		}

		.course-media {
			@apply relative;
			@apply overflow-hidden;
			@apply md:col-start-1 md:col-span-3;
		}

		.course-action {
			@apply absolute bottom-4 left-4 md:bottom-6 md:left-6;
		}

		.content {
			@apply md:col-start-4 md:col-span-2;
			@apply bg-white;
		}

		.course-badge {
			@apply md:absolute md:left-0 md:top-0;
		}

		.course-type {
			@apply uppercase tracking-wide font-semibold;
			@apply text-gray-600 text-sm md:text-base;
		}

		.course-title {
			@apply text-2xl leading-snug;
			@apply text-gray-900 font-medium font-serif;
		}

		.course-type + .course-title {
			@apply mt-3;
		}

		.course-block {
			@apply flex flex-col;
			@apply page-margin py-5;
			@apply bg-white;
			@apply md:px-5;
			@apply md:justify-between;

			@screen md {
				min-height: var(--hero-content-min-height);
			}
		}

		.course-data-container {
			@apply mt-6;
		}

		.course-data {
			/* Space between list items */
			--space-x: theme('spacing.8'); /* 32px */
			--space-y: theme('spacing.4'); /* 20px */

			@apply flex flex-wrap;
			/* Trick to re-create flex-gap for browsers that don't support it */
			margin-left: calc(var(--space-x) * -1);
			margin-top: calc(var(--space-y) * -1);
		}

		.course-data-item {
			@apply flex items-center;
			@apply uppercase tracking-wide text-xs md:text-sm text-gray-900;
			margin-left: var(--space-x);
			margin-top: var(--space-y);

			&.full {
				@apply overflow-hidden;
				@apply w-full;

				.data-text {
					@apply w-full flex-auto;
					@apply overflow-ellipsis overflow-hidden whitespace-nowrap;
				}
			}
		}

		.data-icon {
			@apply inline-flex flex-shrink-0 mr-2;
		}

		.data-text {
			@apply flex-shrink-0;
		}

		.course-price {
			@apply pt-4 mt-5;
			@apply border-t border-gray-600;
		}

		.footer {
			@apply mt-6 mb-8 md:mt-8 md:mb-10;
		}
	}

	@screen md {
		.course-card-wide .course-media:hover :global(.image) {
			@apply transform scale-105;
		}
	}
	.course-card-wide	:global(.image) {
		@apply object-cover object-center;
		@apply transition-transform duration-500 ease-in-out;
		will-change: transform;
	}
</style>
